import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  TablePagination,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { IoSearch } from 'react-icons/io5';
import { MdAdd, MdGroup, MdModeEdit, MdDeleteOutline } from 'react-icons/md';
import { useSnackbar } from 'notistack';
import { getGroups, deleteGroup } from "../api's/ManagementApi";
import AddGroupDialog from '../components/Admin/AddGroupDialog';
import ConfirmDeleteDialog from '../components/Admin/ConfirmDeleteDialog';

function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchGroups();
  }, []);

  const filterGroups = useCallback(() => {
    if (!searchTerm.trim()) {
      setFilteredGroups(groups);
      return;
    }
    const term = searchTerm.toLowerCase();
    const filtered = groups.filter(
      (group) => group.groupName?.toLowerCase().includes(term) || group.description?.toLowerCase().includes(term)
    );
    setFilteredGroups(filtered);
    setPage(0);
  }, [groups, searchTerm]);

  useEffect(() => {
    filterGroups();
  }, [filterGroups]);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await getGroups();
      const data = response.result || response.data?.result || [];
      setGroups(data);
      setFilteredGroups(data);
    } catch (error) {
      console.error('Error fetching groups:', error);
      enqueueSnackbar('Failed to fetch groups', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleOpenAddDialog = () => {
    setGroupToEdit(null);
    setAddDialogOpen(true);
  };

  const handleEditGroup = (group) => {
    setGroupToEdit(group);
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setGroupToEdit(null);
  };

  const handleDeleteGroupClick = (group) => {
    setGroupToDelete(group);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    if (!deleting) {
      setDeleteConfirmOpen(false);
      setGroupToDelete(null);
    }
  };

  const handleConfirmDeleteGroup = async () => {
    if (!groupToDelete) return;
    setDeleting(true);
    try {
      await deleteGroup(groupToDelete.groupId);
      enqueueSnackbar('Group deleted successfully', { variant: 'success' });
      setDeleteConfirmOpen(false);
      setGroupToDelete(null);
      fetchGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Failed to delete group', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleGroupSaved = () => {
    fetchGroups();
  };

  const getRoleChipColor = (roleName) => {
    const name = roleName?.toUpperCase();
    if (name === 'ADMIN') return { bg: '#E3F2FD', color: '#1565C0' };
    if (name === 'SUPER_USER') return { bg: '#F3E5F5', color: '#7B1FA2' };
    if (name === 'READER') return { bg: '#F5F5F5', color: '#616161' };
    if (name === 'EXECUTOR') return { bg: '#E8F5E9', color: '#2E7D32' };
    return { bg: '#FFF3E0', color: '#EF6C00' };
  };

  const formatScopeEntry = (entry) => {
    if (!entry.domainId) return 'All Domains';
    const parts = [];
    parts.push('Domain');
    if (entry.clientId) parts.push('Client');
    if (entry.engagementId) parts.push('Engagement');
    if (entry.workflowId) parts.push('Workflow');
    return parts.join(' → ');
  };

  return (
    <Box sx={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '22px', mb: 0.5 }}>
            Groups
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>
            Manage groups and their hierarchical scope access across the platform.
          </Typography>
        </Box>
        <Button
          type="submit"
          variant="contained"
          sx={{ backgroundColor: '#00043C', '&:hover': { backgroundColor: '#00043C' } }}
          onClick={handleOpenAddDialog}
        >
          <MdAdd />
          Add New Group
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search by group name or description..."
        value={searchTerm}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IoSearch color="#999" />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            borderRadius: '10px',
            '&:hover fieldset': { borderColor: '#00043C' },
            '&.Mui-focused fieldset': { borderColor: '#00043C', borderWidth: '2px' },
          },
        }}
      />

      {/* Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: '1px solid #e8e8e8',
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} thickness={3.5} sx={{ color: '#00043C' }} />
          </Box>
        ) : filteredGroups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <MdGroup size={48} style={{ color: '#00043C', opacity: 0.5 }} />
            <Typography variant="h6" sx={{ color: '#999', mt: 2, fontWeight: 600 }}>
              No groups found
            </Typography>
            <Typography variant="body2" sx={{ color: '#bbb', mt: 0.5, mb: 2 }}>
              {searchTerm ? 'Try a different search term' : 'Create a group to get started'}
            </Typography>
            {!searchTerm && (
              <Button
                variant="contained"
                startIcon={<MdAdd />}
                onClick={handleOpenAddDialog}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  backgroundColor: '#00043C',
                  boxShadow: '0 2px 8px rgba(0, 4, 60, 0.25)',
                  '&:hover': {
                    backgroundColor: '#000652',
                    boxShadow: '0 4px 12px rgba(0, 4, 60, 0.35)',
                  },
                }}
              >
                Create Group
              </Button>
            )}
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#fafafa', borderBottom: '2px solid #e8e8e8' }}>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: '#999',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                      py: 1.5,
                    }}
                  >
                    Group
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: '#999',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                      py: 1.5,
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: '#999',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                      py: 1.5,
                    }}
                  >
                    Roles
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: '#999',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                      py: 1.5,
                      width: 120,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((group) => (
                  <TableRow
                    key={group.groupId}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f0f2ff',
                        '& .edit-group-btn': {
                          backgroundColor: '#fff',
                          borderColor: '#d0d5f0',
                          boxShadow: '0 1px 2px rgba(0, 4, 60, 0.08)',
                        },
                        '& .edit-group-btn:hover': {
                          backgroundColor: '#00043C',
                          color: '#fff',
                          borderColor: '#00043C',
                          transform: 'scale(1.05)',
                          boxShadow: '0 2px 8px rgba(0, 4, 60, 0.35)',
                        },
                      },
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '14px' }}>
                        {group.groupName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#999', fontSize: '11px' }}>
                        Created by {group.createdBy}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, maxWidth: 250 }}>
                      <Typography variant="body2" sx={{ color: '#555', fontSize: '13px' }} noWrap>
                        {group.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {group.roleIds && group.roleIds.length > 0 ? (
                        <Chip
                          label={`${group.roleIds.length} role${group.roleIds.length > 1 ? 's' : ''}`}
                          size="small"
                          sx={{
                            backgroundColor: '#E3F2FD',
                            color: '#1565C0',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: '24px',
                          }}
                        />
                      ) : (
                        <Chip
                          label="No roles"
                          size="small"
                          sx={{
                            backgroundColor: '#FFF3E0',
                            color: '#EF6C00',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: '24px',
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Tooltip title="Edit group" arrow placement="top">
                          <span>
                            <IconButton
                              className="edit-group-btn"
                              size="small"
                              onClick={() => handleEditGroup(group)}
                              sx={{
                                color: '#00043C',
                                backgroundColor: '#f0f2ff',
                                width: '30px',
                                height: '30px',
                                border: '1px solid #e8eaff',
                                borderRadius: '7px',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: '#00043C',
                                  color: '#fff',
                                  borderColor: '#00043C',
                                  transform: 'scale(1.05)',
                                  boxShadow: '0 2px 8px rgba(0, 4, 60, 0.35)',
                                },
                              }}
                            >
                              <MdModeEdit size={15} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete group" arrow placement="top">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteGroupClick(group)}
                              sx={{
                                color: '#d32f2f',
                                backgroundColor: '#ffebee',
                                width: '30px',
                                height: '30px',
                                border: '1px solid #ffcdd2',
                                borderRadius: '7px',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: '#ffcdd2',
                                  borderColor: '#d32f2f',
                                  transform: 'scale(1.05)',
                                  boxShadow: '0 2px 8px rgba(211, 47, 47, 0.25)',
                                },
                              }}
                            >
                              <MdDeleteOutline size={15} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredGroups.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: '1px solid #f0f0f0',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '13px',
                  color: '#666',
                },
              }}
            />
          </>
        )}
      </TableContainer>

      {addDialogOpen && (
        <AddGroupDialog
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          onSave={handleGroupSaved}
          group={groupToEdit}
        />
      )}

      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        onConfirm={handleConfirmDeleteGroup}
        title="Delete group"
        message={
          groupToDelete
            ? `Are you sure you want to delete the group "${groupToDelete.groupName}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        loading={deleting}
      />
    </Box>
  );
}

export default GroupsPage;
